import React, { useCallback, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "./index.scss";
import DropdownMenuFloat from "../DropdownMenuFloat";
import MessageStatus from "../../atoms/MessageStatus";
import ContentWithLinkPreviews from "../ContentWithLinkPreviews";
import { useClickableLinks } from "../../../hooks/useClickableLinks";
import classNames from "../../../utils/classNames";
import { resolveGatewayBackedServiceBaseUrl } from "../../../services/serviceBaseUrl";

const CHAT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);

const ChatBubble = ({
                        msg,
                        currentUserId,
                        formatLocalTime,
                        showDropdown,
                        dropdownCoords,
                        onRightClick,
                        onCloseDropdown,
                        onDeleteMessage,
                        onEditMessage,
                        contentRenderer
                    }) => {

    const { t } = useTranslation();
    const renderMessageWithLinks = useClickableLinks();
    const renderedMessage = useMemo(() => renderMessageWithLinks(msg.content), [msg.content, renderMessageWithLinks]);

    const isUser =
        currentUserId !== undefined &&
        currentUserId !== null &&
        String(msg.senderId).trim() === String(currentUserId).trim();

    const handleDelete = useCallback(async () => {
        try {
            await axios.delete(`${CHAT_SERVICE_URL}/api/chat/message/${msg.id}`, {
                withCredentials: true,
            });
            onDeleteMessage?.(msg.id);
            onCloseDropdown?.();
        } catch (err) {
            console.error("❌ Error deleting message:", err);
        }
    }, [msg.id, onCloseDropdown, onDeleteMessage]);

    const handleEdit = useCallback(async () => {
        const newContent = prompt(t("actions.editMessagePrompt"), msg.content);
        if (!newContent || newContent.trim() === msg.content.trim()) {
            onCloseDropdown?.();
            return;
        }

        try {
            const response = await axios.patch(
                `${CHAT_SERVICE_URL}/api/chat/message/${msg.id}`,
                { content: newContent.trim() },
                { withCredentials: true }
            );
            onEditMessage?.(response.data);
        } catch (err) {
            console.error("❌ Error editing message:", err);
        } finally {
            onCloseDropdown?.();
        }
    }, [msg.content, msg.id, onCloseDropdown, onEditMessage, t]);

    const dropdownItems = useMemo(() => {
        if (isUser) {
            return [
                {
                    label: t("actions.editMessage"),
                    onClick: handleEdit,
                    icon: (
                        <svg viewBox="0 0 20 20" aria-hidden="true">
                            <path
                                fill="currentColor"
                                d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-7.071 7.07-3.535.707.707-3.535z"
                            />
                            <path
                                fill="currentColor"
                                d="M4 15h12a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2z"
                                opacity="0.65"
                            />
                        </svg>
                    ),
                },
                {
                    label: t("actions.deleteMessage"),
                    onClick: handleDelete,
                    icon: (
                        <svg viewBox="0 0 20 20" aria-hidden="true">
                            <path
                                fill="currentColor"
                                d="M7 2a2 2 0 0 0-2 2H3.5a1 1 0 0 0 0 2H4v9a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6h.5a1 1 0 0 0 0-2H15a2 2 0 0 0-2-2zm0 2h6v2H7zm3 4a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1z"
                            />
                        </svg>
                    ),
                    tone: "danger",
                },
            ];
        }

        return [
            {
                label: "Quote message",
                onClick: () => onCloseDropdown?.(),
                icon: (
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                        <path
                            fill="currentColor"
                            d="M6 4a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3H4v2H2v-4a3 3 0 0 1 3-3h1V7a1 1 0 0 0-1-1H2V4zM16 4a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3h-2v2h-2v-4a3 3 0 0 1 3-3h1V7a1 1 0 0 0-1-1h-3V4z"
                        />
                    </svg>
                ),
            },
        ];
    }, [handleDelete, handleEdit, isUser, onCloseDropdown, t]);

    return (
        <>
            <div
                className={classNames(
                    "chat-bubble",
                    isUser ? "chat-bubble--user" : "chat-bubble--friend"
                )}
                onContextMenu={onRightClick}
            >
                <span className="chat-bubble__glow" aria-hidden="true" />
                <div
                    className="chat-bubble__body"
                    onContextMenu={(event) => {
                        window.getSelection()?.removeAllRanges();
                        event.preventDefault();
                    }}
                >
                    <div className="chat-bubble__content">
                        {contentRenderer ? contentRenderer(msg.content) : (
                            <ContentWithLinkPreviews previewUrls={renderedMessage.previews}>
                                <div className="chat-bubble__text">{renderedMessage.content}</div>
                            </ContentWithLinkPreviews>
                        )}
                    </div>
                    <div className="chat-bubble__meta">
                        <span className="chat-bubble__time">
                            {msg.edited && <span className="chat-bubble__edited">edited</span>}
                            {msg.timestamp ? formatLocalTime(msg.timestamp) : ""}
                        </span>
                        {isUser && <MessageStatus status={msg.deliveryStatus} />}
                    </div>
                </div>
            </div>

            {showDropdown && (
                <DropdownMenuFloat
                    position={dropdownCoords}
                    items={dropdownItems}
                    onClose={onCloseDropdown}
                />
            )}
        </>

    );
};

export default ChatBubble;
